import * as Sentry from "@sentry/react";

/**
 * Check analytics consent from localStorage (cookie_consent_v1).
 * Sentry is initialized before React renders, so we read directly from storage.
 *
 * The localStorage key MUST stay in sync with `CONSENT_KEY` in
 * `@hotelb2b/cookie-consent-core/storage.ts` (currently `cookie_consent_v1`).
 * Reading from the old `cookie_consent` key meant Sentry replays were enabled
 * for zero users since the core migration to v1 (architecture M10).
 *
 * Snapshot shape:
 *   { categories: { analytics, marketing, essential }, expiresAt, … }
 */
function hasAnalyticsConsentSync(): boolean {
  try {
    const stored = localStorage.getItem("cookie_consent_v1");
    if (!stored) return false;
    const consent = JSON.parse(stored);
    if (!consent?.expiresAt || new Date(consent.expiresAt) <= new Date()) return false;
    return Boolean(consent?.categories?.analytics);
  } catch {
    return false;
  }
}

/**
 * `?invite=<token>` in a URL — a single-use trial-invite bearer token
 * (spec 2026-09-30-trial-invite-bez-proformy, review MEDIUM-2). Also matches a
 * bare query string (`invite=…`), as in `request.query_string`, an
 * HTML-escaped one (`&amp;invite=…`), and a URL percent-encoded once or more
 * inside another one, wholly or only up to the separator (`%3Finvite%3D…`,
 * `%26invite=…`; e.g. the page address in an analytics request). The value
 * ends at `&`, `#`, whitespace or `%`, which starts an encoded `&`; tokens are
 * base64url, so they hold no `%`.
 */
const INVITE_TOKEN_IN_URL =
  /((?:^|[?&;]|%(?:25)*(?:3f|26))invite(?:=|%(?:25)*3d))[^&#\s%]*/gi;

/** Cheap test for strings that may hold the token, plain or percent-encoded. */
const MENTIONS_INVITE = /invite(?:=|%(?:25)*3d)/i;

/** Nesting limit for scrubSentryPayload — events are plain JSON well within it. */
const MAX_SCRUB_DEPTH = 12;

/** Masks the trial-invite token in a URL (or any string holding one). */
export function scrubInviteToken(value: string): string {
  return value.replace(INVITE_TOKEN_IN_URL, "$1[Filtered]");
}

type Container = Record<string, unknown>;

/**
 * Arrays and plain objects, i.e. the JSON that Sentry sends. By the time
 * beforeSend/beforeSendTransaction run, the SDK has normalised user data
 * (extra, contexts, breadcrumb data) into such objects; the class instances
 * left are its own Scope and Client in `sdkProcessingMetadata`, never sent.
 *
 * beforeBreadcrumb gets raw data, where console arguments can be class
 * instances. Walking into class instances is how KN-FRONTEND-5 reached the
 * SDK client, so a token in an instance's own field passes that hook
 * unmasked, as it does in URL and Error arguments (they keep the URL in
 * accessors and non-enumerable fields). Normalisation turns all three into
 * plain data, masked by beforeSend when an event carries the breadcrumb and
 * by beforeAddRecordingEvent when Session Replay records it.
 */
function isPlainContainer(value: unknown): value is Container {
  if (Array.isArray(value)) return true;
  if (!value || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/** Shallow copy with reassignable data properties; getters are copied, not called. */
function writableCopy(container: Container): Container {
  if (Array.isArray(container)) return container.slice() as unknown as Container;
  const copy: Container = Object.create(Object.getPrototypeOf(container));
  for (const key of Object.keys(container)) {
    const descriptor = Object.getOwnPropertyDescriptor(container, key);
    if (!descriptor) continue;
    Object.defineProperty(copy, key, {
      ...descriptor,
      ...("value" in descriptor ? { writable: true } : {}),
      configurable: true,
    });
  }
  return copy;
}

/**
 * Masks the trial-invite token in every string of a Sentry event or
 * breadcrumb (request.url, headers.Referer, breadcrumb data.from/to/url, span
 * data/description).
 *
 * Never mutates its input and never calls a getter. Returns the same
 * reference when nothing changed, otherwise a copy of each container on the
 * path to a masked string. KN-FRONTEND-5: assigning in place reached the
 * SDK's client through sdkProcessingMetadata, whose promise buffer has a
 * getter-only `$` returning a new array on every read, so
 * beforeSendTransaction threw for every transaction.
 *
 * public/invite-capture.js removes `?invite=` from the URL before Sentry
 * initialises; this is the second line of defence (Sentry has no consent
 * gate and samples every pageload transaction).
 */
export function scrubSentryPayload<T>(
  payload: T,
  depth = 0,
  ancestors = new WeakSet<object>()
): T {
  if (typeof payload === "string") {
    return (MENTIONS_INVITE.test(payload) ? scrubInviteToken(payload) : payload) as T;
  }
  // A container already on the path closes a cycle (only raw breadcrumb data
  // has them). Unrolling it to the depth limit grows exponentially with the
  // references per object, so the reference keeps pointing to the original;
  // once normalisation has cut the cycle, beforeSend and
  // beforeAddRecordingEvent mask what it holds.
  if (depth >= MAX_SCRUB_DEPTH || !isPlainContainer(payload) || ancestors.has(payload)) {
    return payload;
  }
  ancestors.add(payload);
  let copy: Container | undefined;
  for (const key of Object.keys(payload)) {
    // One read through the descriptor; an accessor is skipped, not invoked.
    const descriptor = Object.getOwnPropertyDescriptor(payload, key);
    if (!descriptor || !("value" in descriptor)) continue;
    const scrubbed = scrubSentryPayload(descriptor.value, depth + 1, ancestors);
    if (scrubbed === descriptor.value) continue;
    if (!copy) copy = writableCopy(payload);
    copy[key] = scrubbed;
  }
  // Off the path again: a container shared by siblings is scrubbed in each place.
  ancestors.delete(payload);
  return (copy ?? payload) as T;
}

/**
 * scrubSentryPayload for the before* hooks. If scrubbing throws, the payload
 * is dropped: losing one event or breadcrumb is better than sending a bearer
 * token, and a throwing hook loses the event anyway (Sentry replaces it with
 * an internal error that skips beforeSend).
 */
function scrubOrDrop<T>(payload: T): T | null {
  try {
    return scrubSentryPayload(payload);
  } catch {
    return null;
  }
}

/**
 * Whether this page was loaded from an invite link. invite-capture.js has
 * usually removed the token from the address bar by now, but the navigation
 * timing entry keeps the URL the page was loaded with.
 */
function isInvitePageLoad(): boolean {
  const [navigation] =
    typeof performance !== "undefined" && typeof performance.getEntriesByType === "function"
      ? performance.getEntriesByType("navigation")
      : [];
  return MENTIONS_INVITE.test(window.location.href) || MENTIONS_INVITE.test(navigation?.name ?? "");
}

export function initializeSentry() {
  const environment = process.env.NODE_ENV || "development";
  // Build-time env (CRA) first, then runtime env (Docker inject-env.sh).
  // CI builds with an empty REACT_APP_SENTRY_DSN, so production relies on window._env_.
  const dsn =
    process.env.REACT_APP_SENTRY_DSN ||
    (typeof window !== "undefined"
      ? window._env_?.REACT_APP_SENTRY_DSN
      : undefined);

  if (!dsn) {
    console.warn(
      "REACT_APP_SENTRY_DSN not configured, Sentry will not be initialized"
    );
    return;
  }

  // Only include Session Replay integration when user has given analytics consent (RODO),
  // and not on a page loaded from an invite link: if invite-capture.js could not strip the
  // token, the recording copies the page URL into replay_event.urls, the snapshot's meta
  // href and resolved link hrefs, where no hook reaches.
  type SentryIntegration =
    | ReturnType<typeof Sentry.browserTracingIntegration>
    | ReturnType<typeof Sentry.replayIntegration>;
  const integrations: SentryIntegration[] = [Sentry.browserTracingIntegration()];

  if (typeof window !== "undefined" && hasAnalyticsConsentSync() && !isInvitePageLoad()) {
    integrations.push(
      Sentry.replayIntegration({
        // Mask all text content, input values, and media elements (RODO Art. 5(1)(c))
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        // Breadcrumbs and performance spans in the recording, normalised by
        // Replay after beforeBreadcrumb; DOM snapshots do not pass through it.
        beforeAddRecordingEvent: (event) => scrubOrDrop(event),
      })
    );
  }

  Sentry.init({
    dsn,
    environment,

    // Performance Monitoring - configurable via environment
    tracesSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "1.0"
    ),

    // Session Replay - configurable via environment
    replaysSessionSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0.1"
    ),
    replaysOnErrorSampleRate: 1.0,

    integrations,

    // Release tracking
    release: process.env.REACT_APP_SENTRY_RELEASE || "development",

    // Debug mode - configurable via environment
    debug: process.env.REACT_APP_SENTRY_DEBUG === "true",

    // Error filtering
    beforeSend(event, hint) {
      // Filter out development-only errors
      if (environment === "development") {
        const error = hint.originalException;
        if (error && typeof error === "object" && "message" in error) {
          const errorMessage = String(error.message);
          // Skip common development errors
          if (
            errorMessage.includes("Non-Error promise rejection captured") ||
            errorMessage.includes("ResizeObserver loop limit exceeded") ||
            errorMessage.includes("Network Error")
          ) {
            return null;
          }
        }
      }

      return scrubOrDrop(event);
    },

    beforeSendTransaction(event) {
      return scrubOrDrop(event);
    },

    beforeBreadcrumb(breadcrumb) {
      return scrubOrDrop(breadcrumb);
    },

    // Context and tags
    initialScope: {
      tags: {
        component: "frontend",
        framework: "react",
      },
    },
  });
}

// Helper functions for custom tracking
export function setUserContext(user: {
  id: string;
  email: string;
  hotelId?: string;
  firstName?: string;
  lastName?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username:
      user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : undefined,
    ...(user.hotelId && { hotelId: user.hotelId }),
  });
}

export function addBusinessContext(context: Record<string, unknown>) {
  Sentry.setContext("business", context);
}

export function trackBusinessEvent(
  eventName: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category: "business",
    message: eventName,
    level: "info",
    data,
  });
}

export function captureBusinessError(
  error: Error,
  context?: Record<string, unknown>
) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("business", context);
    }
    Sentry.captureException(error);
  });
}

export function showUserFeedbackDialog() {
  const eventId = Sentry.lastEventId();
  if (eventId) {
    Sentry.showReportDialog({ eventId });
  }
}

// React Error Boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;
